import { createSlice, createSelector } from '@reduxjs/toolkit';
import { getUserFromLocalStorage } from '../../utils/userLocalStorage';

/**
 * Initial auth state hydrating from browser storage
 */
const getInitialAuthState = () => {
  const user = getUserFromLocalStorage();
  const isActiveDoctorAcc = typeof window !== 'undefined'
    ? localStorage.getItem('isAciveDoctorAcc') === 'true' || localStorage.getItem('isAciveDoctorAcc') === '1'
    : false;

  return {
    user: user || null,
    isActiveDoctor: isActiveDoctorAcc,
    isAuthenticated: !!user,
    role: user?.role || (isActiveDoctorAcc ? 'doctor' : 'patient'),
    sessionExpiresAt: null,
  };
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialAuthState(),
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload?.role) {
        state.role = action.payload.role;
      }
    },
    setIsActiveDoctor: (state, action) => {
      state.isActiveDoctor = Boolean(action.payload);
      if (typeof window !== 'undefined') {
        localStorage.setItem('isAciveDoctorAcc', state.isActiveDoctor ? '1' : '0');
      }
    },
    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        if (typeof window !== 'undefined') {
          localStorage.setItem('doctolikLoginInfo', JSON.stringify(state.user));
        }
      }
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isActiveDoctor = false;
      state.role = 'guest';
      state.sessionExpiresAt = null;
    },
  },
});

export const {
  setUser,
  setIsActiveDoctor,
  updateUserProfile,
  logout,
} = authSlice.actions;

// Selectors
export const selectAuthState = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsActiveDoctor = (state) => state.auth.isActiveDoctor;
export const selectUserRole = (state) => state.auth.role;

export const selectDoctorId = createSelector(
  [selectUser, selectIsActiveDoctor],
  (user, isActiveDoctor) => {
    if (!user) return null;
    return user.doctor_id || user.doctorId || (isActiveDoctor ? user.id : null);
  }
);

export const selectPatientId = createSelector(
  [selectUser],
  (user) => user?.patient_id || user?.patientId || user?.id || null
);

export const selectUserDisplayName = createSelector(
  [selectUser],
  (user) => {
    if (!user) return 'Guest';
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.name || user.username || user.email || 'User';
  }
);

export default authSlice.reducer;
